import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Trophy, CalendarCheck, Sprout, ArrowRight, LogOut, Settings, Lock } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { api, localDay, mediaUrl, formatErr } from "@/lib/api";
import { StreakCalendar, Milestones } from "@/components/Streak";
import ResourceCard, { ResourceGrid } from "@/components/ResourceCard";
import WeeklyCard from "@/components/WeeklyCard";
import { Moon, Award, ListMusic, Quote, CreditCard, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function Badges({ list = [], label }) {
  const items = list.filter((c) => c.total);
  if (!items.length) return null;
  return (
    <section data-testid="dashboard-badges">
      <h2 className="mb-5 text-2xl sm:text-3xl">{label}</h2>
      <div className="flex flex-wrap gap-4">
        {items.map((c) => {
          const won = c.days_done.length >= c.total;
          return (
            <Link key={c.slug} to={won ? `/challenge/${c.slug}/certificate` : `/challenge/${c.slug}`} title={won ? "View certificate" : "Finish to earn this badge"}
              className={`flex w-36 flex-col items-center rounded-[24px] border p-4 text-center transition-transform duration-300 hover:-translate-y-0.5 ${won ? "border-bt-primary bg-bt-surface" : "border-dashed border-bt-border opacity-60"}`}
              data-testid={`badge-${c.slug}${won ? "-earned" : "-locked"}`}>
              <span className={`mb-3 flex h-14 w-14 items-center justify-center rounded-full ${won ? "bg-bt-primary text-bt-bg" : "bg-bt-sage text-bt-sagedeep"}`}><Award size={24} /></span>
              <span className="text-sm font-semibold leading-tight text-bt-heading">{c.title}</span>
              <span className="mt-1 text-xs text-bt-sagedeep">{won ? "Completed" : `${c.days_done.length}/${c.total} days`}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

const BedtimeCard = ({ label }) => (
  <Link to="/bedtime" className="flex items-center justify-between gap-5 rounded-[28px] bg-bt-primary p-7 text-bt-bg transition-transform duration-300 hover:-translate-y-0.5" data-testid="dashboard-bedtime">
    <span className="flex items-center gap-4">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15"><Moon size={20} /></span>
      <span><span className="block font-heading text-2xl" style={{ color: "var(--bt-background)" }}>{label}</span><span className="text-sm opacity-85">Play your affirmations back-to-back at bedtime</span></span>
    </span>
    <ArrowRight className="shrink-0" />
  </Link>
);

function ManageBilling({ label, testid = "dashboard-manage-billing" }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e));
      setBusy(false);
    }
  };
  return (
    <button onClick={open} disabled={busy} className="btn-secondary !min-h-[44px] text-sm" data-testid={testid}>
      {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} {label}
    </button>
  );
}

function AffirmationOfDay({ a, label }) {
  const { settings } = useSite();
  const cfg = settings.affirmation_of_day || {};
  if (!a) return null;
  return (
    <section className="relative overflow-hidden rounded-[32px] bg-bt-primary p-7 text-bt-bg sm:p-10" data-testid="dashboard-affirmation">
      <Quote className="absolute -right-4 -top-4 opacity-10" size={160} />
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] opacity-75">{label || cfg.eyebrow}</p>
      <h2 className="font-heading text-3xl sm:text-4xl" style={{ color: "var(--bt-background)" }} data-testid="dashboard-affirmation-title">{a.title}</h2>
      <p className="mt-5 max-w-2xl font-heading text-xl italic leading-snug sm:text-2xl" data-testid="dashboard-affirmation-line">"{a.items?.[0]}"</p>
      <div className="mt-7 flex flex-wrap items-center gap-3">
        {a.audio_url && <audio src={mediaUrl(a.audio_url)} controls className="h-11 max-w-full" data-testid="dashboard-affirmation-audio" />}
        <Link to={`/r/${a.slug}`} className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-bt-bg px-6 text-sm font-semibold text-bt-primary transition-transform hover:-translate-y-0.5" data-testid="dashboard-affirmation-open">{cfg.cta_label} <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

function Playlists({ list = [], label }) {
  if (!list.length) return null;
  return (
    <section data-testid="dashboard-playlists">
      <h2 className="mb-6 text-2xl sm:text-3xl">{label}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {list.map((p) => (
          <Link key={p.slug} to={`/playlist/${p.slug}`} className="group flex items-center gap-5 rounded-[28px] border border-bt-border bg-bt-surface p-6 transition-transform duration-300 hover:-translate-y-0.5" data-testid={`dashboard-playlist-${p.slug}`}>
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-bt-sage text-bt-primary">{p.locked ? <Lock size={20} /> : <ListMusic size={22} />}</span>
            <span className="min-w-0 flex-1">
              <span className="block font-heading text-xl text-bt-heading">{p.title}</span>
              <span className="mt-1 block text-sm">{p.count} affirmations{p.locked ? " · Members only" : " · Play back to back"}</span>
            </span>
            <ArrowRight className="shrink-0 text-bt-primary transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function ChallengeBlock({ c, label }) {
  if (!c || !c.total) return null;
  const n = c.days_done.length;
  const finished = n >= c.total;
  let progress = c.subtitle;
  let cta = "Start day 1";
  if (finished) {
    progress = "Completed. Well done.";
    cta = "View challenge";
  } else if (n) {
    progress = `Day ${n} of ${c.total} done`;
    cta = `Continue: day ${n + 1}`;
  }
  return (
    <section className="flex flex-col gap-5 rounded-[32px] border border-bt-border bg-bt-surface p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9" data-testid="dashboard-challenge">
      <div className="flex-1">
        <p className="eyebrow mb-2">Free challenge</p>
        <h2 className="text-2xl sm:text-3xl">{label || c.title}</h2>
        <div className="mt-4 h-2.5 max-w-md overflow-hidden rounded-full bg-bt-sage"><div className="h-full rounded-full bg-bt-primary" style={{ width: `${(n / c.total) * 100}%` }} /></div>
        <p className="mt-2 text-sm" data-testid="dashboard-challenge-progress">{progress}</p>
      </div>
      <Link to={`/challenge/${c.slug}`} className="btn-primary shrink-0" data-testid="dashboard-challenge-cta">{cta} <ArrowRight size={18} /></Link>
    </section>
  );
}

const Stat = ({ icon: I, label, value, testid }) => (
  <div className="rounded-[22px] bg-bt-surface p-5" data-testid={testid}>
    <I size={18} className="mb-3 text-bt-sagedeep" />
    <p className="font-heading text-4xl text-bt-heading">{value}</p>
    <p className="mt-1 text-sm text-bt-text">{label}</p>
  </div>
);

function StreakSection({ d, label }) {
  const { settings } = useSite();
  const st = settings.streak;
  const s = d.streak;
  if (!d.is_member && settings.membership.tracking_members_only)
    return (
      <section className="rounded-[32px] bg-bt-sage p-8 sm:p-10" data-testid="dashboard-streak-locked">
        <Lock className="mb-4 text-bt-primary" />
        <h2 className="text-3xl">{label}</h2>
        <p className="mt-3 max-w-lg">Track a daily growth activity, celebrate milestones and see your progress grow. {st.motto}</p>
        <Link to="/membership" className="btn-primary mt-6" data-testid="dashboard-unlock-streak">Unlock with membership</Link>
      </section>
    );
  const nextMs = st.milestones.find((m) => m.days > s.current);
  const hitMs = [...st.milestones].reverse().find((m) => s.current >= m.days);
  return (
    <section className="rounded-[32px] bg-bt-sage p-6 sm:p-10" data-testid="dashboard-streak">
      <div className="mb-8 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow mb-2">{st.title}</p>
          <h2 className="text-3xl">{label}</h2>
        </div>
        <p className="font-heading italic text-bt-primary">{st.motto}</p>
      </div>
      {s.missed_yesterday && <p className="mb-6 rounded-2xl bg-bt-surface px-5 py-4 text-bt-heading" data-testid="streak-missed-message">{st.missed_message}</p>}
      {hitMs && s.current > 0 && <p className="mb-6 rounded-2xl bg-bt-primary px-5 py-4 text-bt-bg" data-testid="streak-milestone-message"><b>{hitMs.title}.</b> {hitMs.message}</p>}
      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={Flame} label="Current streak" value={s.current} testid="stat-current-streak" />
            <Stat icon={Trophy} label="Longest streak" value={s.longest} testid="stat-longest-streak" />
            <Stat icon={Sprout} label="Total growth days" value={s.total} testid="stat-total-days" />
            <Stat icon={CalendarCheck} label="Days this month" value={s.this_month} testid="stat-month-days" />
          </div>
          <p className="mb-3 mt-8 text-sm font-semibold text-bt-heading">{nextMs ? `${nextMs.days - s.current} day${nextMs.days - s.current === 1 ? "" : "s"} to "${nextMs.title}"` : "Every milestone reached. Keep growing."}</p>
          <Milestones milestones={st.milestones} longest={s.longest} />
        </div>
        <div className="rounded-[24px] bg-bt-surface p-5"><StreakCalendar days={s.days} /></div>
      </div>
    </section>
  );
}

function Block({ label, items, testid, compact }) {
  if (!items?.length) return null;
  return (
    <section data-testid={testid}>
      <h2 className="mb-6 text-2xl sm:text-3xl">{label}</h2>
      <ResourceGrid items={items} compact={compact} />
    </section>
  );
}

function Today({ d, label }) {
  if (!d.today) return null;
  const done = d.streak.done_today;
  return (
    <section data-testid="dashboard-today">
      <h2 className="mb-6 text-2xl sm:text-3xl">{label}</h2>
      <div className="grid gap-5 md:grid-cols-[1.2fr_1fr] md:items-center">
        <ResourceCard r={d.today} />
        <div className="rounded-[28px] border border-dashed border-bt-sagedeep p-7">
          <p className="font-heading text-2xl text-bt-heading">{done ? "You've grown today." : "One small activity is enough."}</p>
          <p className="mt-2">{done ? "Come back tomorrow, or explore something new." : "Open it, do it, then tap Mark as complete."}</p>
          <Link to={`/r/${d.today.slug}`} className="btn-primary mt-5" data-testid="dashboard-today-start">Start <ArrowRight size={18} /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { settings } = useSite();
  const { user, logout } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/me/dashboard", { params: { today: localDay() } }).then((r) => setD(r.data));
  }, []);
  const sections = useMemo(() => settings.dashboard.sections.filter((s) => s.enabled), [settings.dashboard.sections]);
  if (!d) return <div className="min-h-[60vh]" />;
  const render = {
    affirmation: (l) => <AffirmationOfDay a={d.affirmation} label={l} />,
    playlists: (l) => <Playlists list={d.playlists} label={l} />,
    streak: (l) => <StreakSection d={d} label={l} />,
    badges: (l) => <Badges list={d.challenges} label={l} />,
    bedtime: (l) => <BedtimeCard label={l} />,
    challenge: (l) => {
      const list = (d.challenges || []).filter((c) => c.total);
      const started = list.filter((c) => c.days_done.length > 0 && c.days_done.length < c.total);
      const show = (started.length ? started : list).slice(0, 2);
      return show.length ? <div className="space-y-4">{show.map((c, i) => <ChallengeBlock key={c.slug} c={c} label={i === 0 && l !== "Your challenges" ? l : c.title} />)}</div> : null;
    },
    weekly: (l) => (d.weekly ? <section data-testid="dashboard-weekly"><WeeklyCard issue={d.weekly} headline={l} /></section> : null),
    today: (l) => <Today d={d} label={l} />,
    continue: (l) => <Block label={l} items={d.continue} testid="dashboard-continue" compact />,
    favourites: (l) => <Block label={l} items={d.favourites} testid="dashboard-favourites" compact />,
    recent: (l) => <Block label={l} items={d.recent} testid="dashboard-recent" compact />,
    new: (l) => <Block label={l} items={d.new} testid="dashboard-new" />,
    recommended: (l) => <Block label={l} items={d.recommended} testid="dashboard-recommended" />,
  };
  return (
    <div className="container-bt py-12 sm:py-16" data-testid="dashboard-page">
      <div className="mb-12 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow mb-3">{d.is_member ? settings.membership.name : "Free account"}</p>
          <h1 className="text-4xl sm:text-5xl" data-testid="dashboard-welcome">{settings.dashboard.welcome}, {user.first_name}</h1>
        </div>
        <div className="flex gap-2">
          {user.role === "admin" && <Link to="/admin" className="btn-secondary !min-h-[44px] text-sm" data-testid="dashboard-admin-link"><Settings size={16} /> Admin</Link>}
          {d.has_billing && <ManageBilling label={settings.membership.billing_label} />}
          <button onClick={logout} className="btn-ghost" data-testid="logout-button"><LogOut size={16} /> Sign out</button>
        </div>
      </div>
      {(d.billing_issue || d.cancel_at_period_end) && (
        <div className={`mb-10 flex flex-col items-start gap-4 rounded-[24px] sm:flex-row sm:items-center border p-5 ${d.billing_issue ? "border-red-200 bg-red-50 text-red-900" : "border-bt-border bg-bt-surface"}`} data-testid={d.billing_issue ? "dashboard-billing-issue" : "dashboard-cancelling"}>
          <AlertCircle size={20} className="shrink-0" />
          <p className="flex-1 text-sm sm:text-base">{d.billing_issue ? settings.membership.billing_issue_message : settings.membership.cancelling_message}</p>
          <ManageBilling label={settings.membership.billing_label} testid="dashboard-billing-banner-btn" />
        </div>
      )}
      <div className="space-y-16">
        {sections.filter((s) => render[s.key]).map((s) => <div key={s.key}>{render[s.key](s.label)}</div>)}
        {!d.is_member && (
          <div className="rounded-[28px] border border-bt-border bg-bt-surface p-8 text-center" data-testid="dashboard-upgrade">
            <p className="font-heading text-2xl text-bt-heading">Ready for the full library?</p>
            <Link to="/membership" className="btn-primary mt-5">See membership</Link>
          </div>
        )}
      </div>
    </div>
  );
}
